import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  X,
  PaperPlaneTilt,
  User,
  Envelope,
  Phone,
  MapPin,
  SpinnerGap,
  CheckCircle,
  FilePdf,
  Warning,
  Percent,
  Lightning
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';

const SendQuoteModal = ({ isOpen, onClose, estimate, formData, adjustedPrices, profitMargin }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [useAdjustedPrices, setUseAdjustedPrices] = useState(!!adjustedPrices);
  
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    project_address: ''
  });
  
  const [customMessage, setCustomMessage] = useState('');
  const [validityDays, setValidityDays] = useState(30);

  // Update useAdjustedPrices when adjustedPrices prop changes
  useEffect(() => {
    if (adjustedPrices) {
      setUseAdjustedPrices(true);
    }
  }, [adjustedPrices]);

  // Get the actual prices to use (original or adjusted)
  const getDisplayPrices = () => {
    if (useAdjustedPrices && adjustedPrices) {
      return adjustedPrices;
    }
    if (!estimate?.price_range) {
      return { low: 0, mid: 0, high: 0 };
    }
    return {
      low: estimate.price_range.low,
      mid: estimate.price_range.mid,
      high: estimate.price_range.high
    };
  };

  const displayPrices = getDisplayPrices();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerData.name || !customerData.email) {
      toast({
        title: 'Missing Information',
        description: 'Please provide customer name and email',
        variant: 'destructive'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerData.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        customer: customerData,
        job_type: formData.job_type,
        location: formData.location || '',
        description: formData.description,
        price_low: displayPrices.low,
        price_mid: displayPrices.mid,
        price_high: displayPrices.high,
        timeline_min_days: estimate.timeline_estimate.min_days,
        timeline_max_days: estimate.timeline_estimate.max_days,
        timeline_description: estimate.timeline_estimate.description || '',
        breakdown: estimate.breakdown,
        factors: estimate.factors_affecting_price || [],
        recommendations: estimate.recommendations || [],
        notes: estimate.notes,
        custom_message: customMessage || null,
        validity_days: validityDays,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        materials_included: formData.materials_included,
        profit_margin_applied: useAdjustedPrices ? profitMargin : null
      };

      const response = await axiosInstance.post('/quotes', payload);
      
      if (response.data.success) {
        setSuccess(true);
        setQuoteNumber(response.data.quote_number);
        toast({
          title: 'Quote Sent!',
          description: response.data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send quote',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setQuoteNumber('');
    setCustomerData({ name: '', email: '', phone: '', project_address: '' });
    setCustomMessage('');
    setValidityDays(30);
    setUseAdjustedPrices(!!adjustedPrices);
    onClose();
  };

  if (!isOpen) return null;
  if (!estimate) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="send-quote-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-neutral-800 border-2 border-black rounded-xl shadow-[8px_8px_0px_#000] overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-4 bg-gradient-to-r from-[#2B4C6F] to-[#203A55] text-white border-b-2 border-black">
          <div className="flex items-center gap-3">
            <PaperPlaneTilt size={24} weight="fill" />
            <h2 className="text-xl font-black">Send Quote to Customer</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        {success ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle size={48} weight="fill" className="text-emerald-500" />
            </div>
            <h3 className="text-2xl font-black text-neutral-900 dark:text-white mb-2">
              Quote Sent Successfully!
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Quote <span className="font-bold text-[#2B4C6F]">{quoteNumber}</span> has been emailed to {customerData.email}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 mb-6">
              <FilePdf size={18} weight="fill" className="text-red-500" />
              PDF attached to email
            </div>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleClose}
                className="px-6 h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000]"
              >
                Done
              </Button>
              <Button
                onClick={() => window.open('/quotes', '_blank')}
                variant="outline"
                className="px-6 h-12 font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000]"
              >
                View All Quotes
              </Button>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Price Summary */}
            <div className="bg-neutral-50 dark:bg-neutral-700 rounded-xl p-4 border-2 border-neutral-200 dark:border-neutral-600">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Quote Amount</p>
                {adjustedPrices && profitMargin > 0 && (
                  <button
                    type="button"
                    onClick={() => setUseAdjustedPrices(!useAdjustedPrices)}
                    className={`flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full transition-all ${
                      useAdjustedPrices 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-neutral-200 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-300'
                    }`}
                    data-testid="toggle-margin-btn"
                  >
                    <Percent size={12} weight="bold" />
                    {profitMargin}% Margin {useAdjustedPrices ? 'ON' : 'OFF'}
                  </button>
                )}
              </div>
              
              <p className="text-2xl font-black text-neutral-900 dark:text-white">
                ${displayPrices.low.toLocaleString()} - ${displayPrices.high.toLocaleString()}
              </p>
              
              {useAdjustedPrices && adjustedPrices && profitMargin > 0 && estimate?.price_range && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                    <Lightning size={14} weight="fill" />
                    +{profitMargin}% profit margin applied
                  </span>
                  <span className="text-neutral-400">|</span>
                  <span className="text-neutral-500 line-through">
                    ${estimate.price_range.low.toLocaleString()} - ${estimate.price_range.high.toLocaleString()}
                  </span>
                </div>
              )}
              
              <p className="text-sm text-neutral-500 mt-1">
                {formData.job_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {formData.location || 'No location'}
              </p>
            </div>

            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                <User size={18} weight="bold" className="text-[#2B4C6F]" />
                Customer Information
              </h3>
              
              <div>
                <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                  Name *
                </Label>
                <Input
                  name="name"
                  value={customerData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="h-12 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl"
                  data-testid="customer-name-input"
                  required
                />
              </div>

              <div>
                <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                  <Envelope size={14} className="inline mr-1" />
                  Email *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={customerData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="h-12 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl"
                  data-testid="customer-email-input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                    <Phone size={14} className="inline mr-1" />
                    Phone
                  </Label>
                  <Input
                    name="phone"
                    value={customerData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-12 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div>
                  <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                    Valid For
                  </Label>
                  <select
                    value={validityDays}
                    onChange={(e) => setValidityDays(parseInt(e.target.value))}
                    className="w-full h-12 px-3 bg-white dark:bg-neutral-700 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl font-medium text-neutral-900 dark:text-white"
                  >
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={45}>45 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                  <MapPin size={14} className="inline mr-1" />
                  Project Address
                </Label>
                <Input
                  name="project_address"
                  value={customerData.project_address}
                  onChange={handleChange}
                  placeholder="123 Main St, City, State"
                  className="h-12 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl"
                  data-testid="project-address-input"
                />
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <Label className="font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 block">
                Personal Message (Optional)
              </Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal note to your customer..."
                className="min-h-[80px] border-2 border-neutral-300 dark:border-neutral-600 rounded-xl resize-none"
                data-testid="custom-message-input"
              />
            </div>

            {/* What's Included */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                What's included in the quote:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-blue-500" />
                  Price range breakdown (low/mid/high)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-blue-500" />
                  Cost breakdown (labor, materials, permits)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-blue-500" />
                  Timeline estimate
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-blue-500" />
                  Professional PDF attachment
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                className="flex-1 h-12 font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000]"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="modal-send-quote-btn"
              >
                {loading ? (
                  <><SpinnerGap size={20} className="animate-spin mr-2" /> Sending...</>
                ) : (
                  <><PaperPlaneTilt size={20} weight="fill" className="mr-2" /> Send Quote</>
                )}
              </Button>
            </div>

            <p className="text-xs text-neutral-500 text-center">
              Customer will receive an email with the full estimate and PDF attachment
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SendQuoteModal;
